import {ApolloClient, HttpLink, InMemoryCache, from} from '@apollo/client'
import {ErrorResponse, onError} from '@apollo/client/link/error'
import {enqueueSnackbar} from 'notistack'

import {useMeStore} from '@progyny/store/meStore'
import {REACT_APP_GRAPHCMS_ENDPOINT_URL} from '@progyny/utils/env'

const buildClient = (authToken: string | undefined): ApolloClient<any> => {
  const errorHandler = ({graphQLErrors, networkError}: ErrorResponse) => {
    if (graphQLErrors) {
      for (const err of graphQLErrors) {
        enqueueSnackbar(err.message, {variant: 'error'})
      }
    }

    if (networkError) {
      enqueueSnackbar(networkError.message, {variant: 'error'})
    }
  }

  return new ApolloClient({
    link: from([
      onError(errorHandler),
      new HttpLink({
        uri: REACT_APP_GRAPHCMS_ENDPOINT_URL,
        headers: {authorization: `${authToken}`},
      }),
    ]),
    cache: new InMemoryCache(),
  })
}

// Module-level cache: one client instance per token value.
// Seeded at module load so the client is never null.
let cachedToken: string | undefined = undefined
let cachedClient: ApolloClient<any> = buildClient(undefined)

const getOrCreateClient = (
  authToken: string | undefined
): ApolloClient<any> => {
  if (cachedToken !== authToken) {
    cachedToken = authToken
    cachedClient = buildClient(authToken)
  }
  return cachedClient
}

/**
 * Returns the CMS Apollo client for the current user's auth token.
 *
 * The client is derived synchronously during render from useMeStore(),
 * matching the same-render guarantee that the old CMSClientProvider useMemo
 * provided. The client is never null.
 */
export const useCMSClient = (): ApolloClient<any> => {
  const user = useMeStore((s) => s.user)
  return getOrCreateClient(user?.hygraphAuthToken)
}
